import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import type { ImobilizadoTipo } from '@triade/contracts';
import { useImobilizados } from '@/api/imobilizados';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { useDebounced } from '@/hooks/use-debounced';
import { formatarMoeda, formatarQuantidade } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { BotaoRelatorios } from '@/relatorios';
import { ImobilizadoDetalhe, type BuscaImobilizados } from './imobilizado-detalhe';
import { TIPO_ROTULO } from './imobilizado-form';

const SITUACOES = { ATIVOS: 'Ativos', INATIVOS: 'Inativos', TODOS: 'Todos' } as const;
type Situacao = keyof typeof SITUACOES;
const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaImobilizados(bruto: Record<string, unknown>): BuscaImobilizados {
  const texto = typeof bruto.q === 'number' ? String(bruto.q) : bruto.q;
  const q = typeof texto === 'string' && texto.trim() ? texto.trim().slice(0, 100) : undefined;
  const itipo = bruto.itipo === 'MAQUINAS_EQUIPAMENTOS' || bruto.itipo === 'IMOVEL' || bruto.itipo === 'VEICULO' ? bruto.itipo : undefined;
  const isit = bruto.isit === 'INATIVOS' || bruto.isit === 'TODOS' ? bruto.isit : undefined;
  const isemlimite = bruto.isemlimite === 'SIM' ? bruto.isemlimite : undefined;
  const id = typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id) ? bruto.id : undefined;
  return {
    ...(q ? { q } : {}),
    ...(itipo ? { itipo } : {}),
    ...(isit ? { isit } : {}),
    ...(isemlimite ? { isemlimite } : {}),
    ...(id ? { id } : {}),
  };
}

/** Tela "Imobilizados" (/cadastros/imobilizados): lista com busca, tipo e situacao; registro abre por `?id=`. */
export function ImobilizadosPage({ busca }: { busca: BuscaImobilizados }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('centro.editar');
  const verCusto = pode('custo.ver');
  const podeCriar = podeEditar && verCusto;
  const situacao: Situacao = busca.isit ?? 'ATIVOS';

  /** `semBloqueio`: so a sincronizacao do `q` do debounce (troca o parametro da mesma tela, nada se perde); o resto segue o bloqueio. */
  const mudar = (mudanca: Partial<BuscaImobilizados>, opcoes?: { semBloqueio?: boolean }) => {
    void navigate({ to: '/cadastros/imobilizados', search: (anterior) => ({ ...anterior, ...mudanca }), replace: true, ...(opcoes?.semBloqueio ? { ignoreBlocker: true } : {}) });
  };
  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtrasado !== (busca.q ?? '')) mudar({ q: termoAtrasado || undefined }, { semBloqueio: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtrasado]);

  const lista = useImobilizados({
    ...(termoAtrasado ? { q: termoAtrasado } : {}),
    ...(busca.itipo ? { tipo: busca.itipo } : {}),
    ...(situacao === 'TODOS' ? {} : { ativo: situacao === 'ATIVOS' }),
    ...(busca.isemlimite ? { semLimite: true } : {}),
    limit: 200,
  });
  const itens = lista.data ?? [];
  const pagItens = usePaginarLista(itens);

  /** Contador dos que depreciam sem limite (ativos): sempre consultado (hook incondicional); so aparece quando ha ao menos 1. */
  const semLimite = useImobilizados({ semLimite: true, ativo: true, limit: 200 });
  const qtdSemLimite = (semLimite.data ?? []).filter((i) => i.semLimiteDepreciacao).length;

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os imobilizados.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os imobilizados" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando imobilizados…</Mensagem>;
  else if (itens.length === 0) {
    const filtrado = Boolean(busca.q) || Boolean(busca.itipo) || Boolean(busca.isemlimite) || situacao !== 'ATIVOS';
    corpo = filtrado ? (
      <Mensagem papel="status">
        Nenhum imobilizado com esses filtros.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => void navigate({ to: '/cadastros/imobilizados', search: {}, replace: true })}>
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhum imobilizado cadastrado.
        {podeCriar && (
          <>
            {' '}
            <Link to="/cadastros/imobilizados" search={{ id: 'novo' }} className="font-medium text-primary hover:underline">
              Cadastre o primeiro
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-small" aria-label="Lista de imobilizados">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Descrição</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Tipo</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Identificador</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Quantidade</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Vida útil</th>
              {verCusto && <th scope="col" className="px-3 py-2.5 font-medium">Valor unitário</th>}
              {verCusto && <th scope="col" className="px-3 py-2.5 font-medium">Depreciação mensal</th>}
              <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {pagItens.visiveis.map((i) => (
              <tr
                key={i.id}
                data-testid="linha-imobilizado"
                onClick={() => void navigate({ to: '/cadastros/imobilizados', search: (a) => ({ ...a, id: i.id }) })}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.id === i.id && 'bg-line-soft', i.semLimiteDepreciacao && 'bg-warning-bg')}
              >
                <td className="px-4 py-3 font-semibold">
                  <div className="flex flex-col items-start gap-1">
                    <span>{i.descricao}</span>
                    {i.semLimiteDepreciacao && (
                      <span data-testid="selo-sem-limite" className="rounded-full bg-warning-bg px-2 py-0.5 text-caption font-semibold text-warning-ink">
                        Sem limite de depreciação: informe a data de aquisição ou a data limite
                      </span>
                    )}
                    {!i.depreciavel && i.observacao && <span className="text-caption text-ink-muted">{i.observacao}</span>}
                  </div>
                </td>
                <td className="px-3 py-3 text-ink-secondary">{TIPO_ROTULO[i.tipo]}</td>
                <td className="px-3 py-3 text-ink-secondary">{i.identificador ?? '—'}</td>
                <td className="tabular px-3 py-3 text-ink-secondary">{formatarQuantidade(i.quantidade)}</td>
                <td className="tabular px-3 py-3 text-ink-secondary">{i.vidaUtilMeses} meses</td>
                {verCusto && <td className="tabular px-3 py-3 text-ink-secondary">{formatarMoeda(i.valorUnitario)}</td>}
                {verCusto && <td className="tabular px-3 py-3 text-ink-secondary">{formatarMoeda(i.depreciacaoMensal)}</td>}
                <td className="px-4 py-3">
                  <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', i.ativo ? 'bg-success-bg text-success-ink' : 'bg-line-soft text-ink-body')}>
                    {i.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span> Imobilizados
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Imobilizados</h1>
        </div>
      </div>

      <section aria-label="Lista de imobilizados" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
          <div className="flex h-[34px] w-[260px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
            <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
            <label htmlFor="imo-q" className="sr-only">
              Buscar imobilizado
            </label>
            <input id="imo-q" type="search" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Descrição, identificador ou marca" autoComplete="off" className="w-full min-w-0 bg-transparent text-small outline-none" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="imo-tipo" className="text-small text-ink-secondary">Tipo</label>
              <select
                id="imo-tipo"
                value={busca.itipo ?? ''}
                onChange={(e) => mudar({ itipo: e.target.value === '' ? undefined : (e.target.value as ImobilizadoTipo) })}
                className={CAMPO}
              >
                <option value="">Todos</option>
                {(Object.keys(TIPO_ROTULO) as ImobilizadoTipo[]).map((t) => (
                  <option key={t} value={t}>
                    {TIPO_ROTULO[t]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="imo-situacao" className="text-small text-ink-secondary">Situação</label>
              <select id="imo-situacao" value={situacao} onChange={(e) => mudar({ isit: e.target.value === 'ATIVOS' ? undefined : (e.target.value as 'INATIVOS' | 'TODOS') })} className={CAMPO}>
                {(Object.keys(SITUACOES) as Situacao[]).map((s) => (
                  <option key={s} value={s}>
                    {SITUACOES[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="imo-limite" className="text-small text-ink-secondary">Limite de depreciação</label>
              <select id="imo-limite" value={busca.isemlimite ?? ''} onChange={(e) => mudar({ isemlimite: e.target.value === '' ? undefined : 'SIM' })} className={CAMPO}>
                <option value="">Todos</option>
                <option value="SIM">Sem limite de depreciação</option>
              </select>
            </div>
          </div>
        </div>
        {qtdSemLimite > 0 && (
          <div role="status" className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-warning-bg px-4 py-2.5 text-small text-warning-ink">
            <span>
              {qtdSemLimite} {qtdSemLimite === 1 ? 'imobilizado' : 'imobilizados'} {qtdSemLimite === 1 ? 'deprecia' : 'depreciam'} sem limite: informe a data de aquisição ou a data limite.
            </span>
            {busca.isemlimite !== 'SIM' && (
              <button type="button" className="font-medium text-warning-ink underline-offset-2 hover:underline" onClick={() => mudar({ isemlimite: 'SIM' })}>
                Ver somente esses
              </button>
            )}
          </div>
        )}
        {corpo}
      </section>

      {busca.id === 'novo' && !podeCriar ? (
        <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-body" role="status">
          Seu acesso não permite cadastrar imobilizados (o cadastro exige o valor, visível só com custo).
        </section>
      ) : (
        busca.id && <ImobilizadoDetalhe key={busca.id} id={busca.id === 'novo' ? undefined : busca.id} busca={busca} podeEditar={podeEditar} verCusto={verCusto} />
      )}

      {/* Lista: Novo, total e Relatorios na barra. Some com o registro aberto (ele tem a propria barra de registro). */}
      {!busca.id && lista.data && (
        <BarraDeAcoesLista
          novo={podeCriar ? { rotulo: 'Novo imobilizado', onClick: () => void navigate({ to: '/cadastros/imobilizados', search: { ...busca, id: 'novo' } }) } : undefined}
          paginacao={pagItens.paginacao}
          relatorios={<BotaoRelatorios tela="imobilizados" />}
        />
      )}
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
